"use client"

import { useState, useEffect, useCallback } from "react"
import type { SyncConfiguration, ConnectorsConfig, SyncConfigData, LoadingState, LocalStorageData } from "../types/sync"
import { useLocalStorage } from "./useLocalStorage"

const DEFAULT_VALUES = {
  CONNECTOR_TYPE: "hubspot-salesforce",
  SYNC_DIRECTION: "Bidirectional",
  SYNC_OPTION: "contacts-leads",
  TENANT_ID: "12345687",
  CONNECTOR_ID: "1",
  CONNECTOR_FROM: "HubSpot",
  CONNECTOR_TO: "Salesforce",
} as const

export const useSyncConfiguration = () => {
  const [syncConfig, setSyncConfig] = useState<SyncConfiguration | null>(null)
  const [syncConfigPayload, setSyncConfigPayload] = useState<ConnectorsConfig | null>(null)
  const [connectorId, setConnectorId] = useState<string>("1")
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: true,
    isStartingSync: false,
    isStartingSynAndSave: false,
  })
  const [error, setError] = useState<string | null>(null)

  const { getItem } = useLocalStorage()

  const getLocalStorageData = useCallback(
    (): LocalStorageData => ({
      selectedConnector: getItem("selectedConnector"),
      selectedSyncOption: getItem("selectedSyncOption"),
      hubspotTenantId: getItem("hubspot_tenant_id"),
      modifysyncConfiguration: getItem("modifysyncConfiguration"),
      selectedSyncDirection: getItem("selectedSyncDirection"),
      fieldMappings: getItem("fieldMappings"),
    }),
    [getItem],
  )

  const parseFieldMappings = useCallback((fieldMappingsStr: string | null) => {
    try {
      return JSON.parse(fieldMappingsStr || "[]")
    } catch (error) {
      console.error("Error parsing field mappings:", error)
      return []
    }
  }, [])

  const parseSyncConfiguration = useCallback((configStr: string | null): SyncConfigData | null => {
    if (!configStr) return null
    try {
      return JSON.parse(configStr)
    } catch (error) {
      console.error("Error parsing sync configuration:", error)
      return null
    }
  }, [])

  const createFieldMappingsObject = useCallback((fieldMappings: any[]) => {
    return fieldMappings.reduce(
      (acc, element) => {
        acc[element.hubspotField] = element.salesforceField
        return acc
      },
      {} as Record<string, string>,
    )
  }, [])

  const loadConfiguration = useCallback(async () => {
    try {
      setLoadingState((prev) => ({ ...prev, isLoading: true }))
      setError(null)

      const localData = getLocalStorageData()
      const fieldMappings = parseFieldMappings(localData.fieldMappings)
      const modifySyncConfig = parseSyncConfiguration(localData.modifysyncConfiguration)

      if (localData.selectedSyncOption) {
        setConnectorId(localData.selectedSyncOption)
      }

      const config: SyncConfiguration = {
        connector: {
          from: DEFAULT_VALUES.CONNECTOR_FROM,
          to: DEFAULT_VALUES.CONNECTOR_TO,
          type: localData.selectedConnector || DEFAULT_VALUES.CONNECTOR_TYPE,
        },
        direction: localData.selectedSyncDirection || DEFAULT_VALUES.SYNC_DIRECTION,
        fieldMappings: fieldMappings.map((mapping: any) => ({
          hubspotField: mapping.hubspotField,
          salesforceField: mapping.salesforceField,
        })),
        syncOption: localData.selectedSyncOption || DEFAULT_VALUES.SYNC_OPTION,
      }

      const payloadSyncConfigPayload: ConnectorsConfig = {
        tenantId: localData.hubspotTenantId || DEFAULT_VALUES.TENANT_ID,
        connectorId: parseInt(localData.selectedSyncOption || DEFAULT_VALUES.CONNECTOR_ID),
        syncDirection: localData.selectedSyncDirection || DEFAULT_VALUES.SYNC_DIRECTION,
        syncConfig: {
          hs_to_sf: modifySyncConfig?.hs_to_sf || {
            create_enabled: false,
            update_enabled: false,
            delete_enabled: false,
            restore_enabled: false,
          },
          sf_to_hs: modifySyncConfig?.sf_to_hs || {
            create_enabled: false,
            update_enabled: false,
            delete_enabled: false,
            restore_enabled: false,
          },
        },
        fieldMappings: createFieldMappingsObject(fieldMappings),
        activate: true
      }

      setSyncConfig(config)
      setSyncConfigPayload(payloadSyncConfigPayload)
    } catch (error) {
      console.error("Error loading configuration:", error)
      setError("Failed to load configuration. Please try again.")
    } finally {
      setLoadingState((prev) => ({ ...prev, isLoading: false }))
    }
  }, [getLocalStorageData, parseFieldMappings, parseSyncConfiguration, createFieldMappingsObject])

  const setStartingSyncState = useCallback((isStarting: boolean, saveOrSync:boolean) => {
      setLoadingState((prev) => ({ ...prev, isStartingSync: isStarting , isStartingSynAndSave : saveOrSync}))
  }, [])

  useEffect(() => {
    loadConfiguration()
  }, [loadConfiguration])

  return {
    syncConfig,
    syncConfigPayload,
    connectorId,
    loadingState,
    error,
    setStartingSyncState,
    reloadConfiguration: loadConfiguration,
  }
}
